/*
 * Kinsora PDP Image Lightbox — custom, zero-dependency
 * ----------------------------------------------------
 * Click/tap the main product image → full-screen lightbox with:
 *   • Tap/click to zoom 2x at that point, tap again to zoom out
 *   • Swipe left/right on mobile to change images
 *   • Swipe down on mobile to close
 *   • Arrow buttons + keyboard arrows on desktop
 *   • Bottom bar (counter, zoom, close) sits directly under the image —
 *     no absolute-positioning hacks, it just flows beneath
 *
 * Data source: the hidden .kinsora-pswp-gallery anchors rendered by the
 * product section (href + data-pswp-srcset + data-global-index + data-pswp-alt).
 * No hidden <img> nodes are embedded, so the browser does not download a
 * redundant 600px copy of every gallery image before the lightbox opens.
 */
(function () {
    'use strict';

    var MIN_SCALE = 1;
    var MAX_SCALE = 4;
    var DOUBLE_TAP_SCALE = 2.5;

    var STATE = {
        overlay: null,
        images: [],       // [{fullSrc, srcset, alt}]
        index: 0,
        // Transform state — single source of truth for image position/zoom
        scale: 1,
        tx: 0,
        ty: 0,
        pinch: null,      // active two-finger pinch
        touch: null,      // active one-finger gesture
        bodyOverflow: '',
        returnFocus: null,
        lastTouchAt: 0,
        resumeInline: false
    };

    /* ---------- inline CSS (injected once) ---------- */
    function injectStyles() {
        if (document.getElementById('kinsora-lightbox-styles')) return;
        var css =
            /* Keep Liquid anchor list hidden (data source only) */
            '.kinsora-pswp-gallery{display:none!important}' +
            '#mainMediaContainer #galleryCount{top:12px;left:12px;right:auto;bottom:auto;pointer-events:none}' +
            /* PDP main image zoom cursor */
            '.main-image.kinsora-zoomable{cursor:zoom-in;position:relative}' +
            '.main-image.kinsora-zoomable img{cursor:zoom-in}' +
            '@media (hover:none){.main-image.kinsora-zoomable,.main-image.kinsora-zoomable img{cursor:default}}' +
            /* Tap-to-zoom hint pill — side configurable via data-zoom-hint-side */
            '.kinsora-zoom-hint{position:absolute;bottom:14px;right:14px;left:auto;display:inline-flex;align-items:center;gap:6px;padding:8px 12px;background:rgba(17,17,17,.78);color:#fff;font-size:12px;letter-spacing:.02em;border-radius:999px;pointer-events:none;opacity:0;transition:opacity .3s ease;z-index:3;-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px)}' +
            '.kinsora-zoom-hint--left{left:14px;right:auto}' +
            '.kinsora-zoom-hint.is-visible{opacity:1}' +
            '.kinsora-zoom-hint svg{width:14px;height:14px;flex:0 0 14px}' +
            /* Full viewport stage with a stable bottom toolbar for all media. */
            '.klb-overlay{position:fixed;inset:0;height:100%;height:100dvh;box-sizing:border-box;z-index:99999;background:rgba(10,10,10,.97);display:flex;flex-direction:column;align-items:stretch;justify-content:flex-start;opacity:0;transition:opacity 200ms ease;-webkit-tap-highlight-color:transparent;overscroll-behavior:contain;touch-action:none}' +
            '.klb-overlay *{touch-action:none}' +
            '.klb-overlay.is-open{opacity:1}' +
            '.klb-stage{position:relative;flex:1 1 0;min-height:0;overflow:hidden;display:flex;align-items:center;justify-content:center;width:100%}' +
            '.klb-track{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;transition:transform 280ms cubic-bezier(.22,.61,.36,1);width:100%;height:100%;max-width:100%}' +
            '.klb-track.is-dragging{transition:none}' +
            /* Image: explicit width/height set by JS (fitImageAndStage) so every image fills the available space regardless of source resolution */
            '.klb-img{display:block;flex:none;max-width:none;max-height:none;object-fit:contain;transform-origin:50% 50%;transition:transform 280ms cubic-bezier(.22,.61,.36,1);will-change:transform;user-select:none;-webkit-user-drag:none;cursor:zoom-in}' +
            '.klb-img.is-zoomed{cursor:zoom-out}' +
            /* Navigation buttons in the shared bottom toolbar */
            '.klb-arrow{position:static;flex:0 0 44px;width:44px;height:44px;border-radius:50%;background:rgba(0,0,0,.55);border:1px solid rgba(255,255,255,.18);color:#fff;font-size:22px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;z-index:2;padding:0;transition:background 150ms ease}' +
            '.klb-arrow:hover{background:rgba(0,0,0,.8)}' +
            '.klb-navigation{display:flex;align-items:center;gap:10px}.klb-arrow svg{width:20px;height:20px}' +
            '.klb-arrow:focus-visible,.klb-btn:focus-visible{outline:2px solid #fff;outline-offset:3px}' +
            '.klb-arrow[disabled]{opacity:.4;cursor:default}' +
            /* Controls stay beneath the stage and clear of native video controls. */
            '.klb-controls{flex:0 0 auto;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 22px;padding-bottom:calc(14px + env(safe-area-inset-bottom,0px));background:rgba(0,0,0,.6);color:#fff;font-family:"Newsreader",Georgia,"Times New Roman",serif}' +
            '.klb-counter{text-align:center;font-size:13px;letter-spacing:.04em;opacity:.9;min-width:52px}' +
            '.klb-actions{display:flex;align-items:center;justify-content:flex-end;gap:10px;min-width:98px}' +
            '.klb-btn{appearance:none;background:transparent;border:1px solid rgba(255,255,255,.28);color:#fff;width:44px;height:44px;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;transition:background 150ms ease,border-color 150ms ease}' +
            '.klb-btn:hover{background:rgba(255,255,255,.08);border-color:rgba(255,255,255,.5)}' +
            '.klb-btn svg{width:18px;height:18px;stroke:#fff;stroke-width:2;fill:none;stroke-linecap:round;stroke-linejoin:round}' +
            /* Compact mobile toolbar; retain navigation for both media types */
            '@media (max-width:767px){.klb-controls{padding:12px 18px;padding-bottom:calc(12px + env(safe-area-inset-bottom,0px))}.klb-counter{font-size:12px}}' +
            /* Prevent body scroll while open */
            '.klb-overlay [hidden]{display:none!important}' +
            '.klb-video{display:block;width:100%;height:100%;max-height:100%;object-fit:contain;touch-action:auto}' +
            '.klb-video-error{position:absolute;top:50%;color:#fff;padding:20px;text-align:center}' +
            '#mainMediaContainer .ks-gallery-video-expand{position:absolute;top:12px;right:12px;z-index:5;display:flex;align-items:center;justify-content:center;width:44px;height:44px;padding:0;border:1px solid rgba(255,255,255,.8);border-radius:50%;color:#fff;background:rgba(47,47,46,.75);cursor:pointer}' +
            '#mainMediaContainer .ks-gallery-video-expand:focus-visible{outline:2px solid #fff;outline-offset:3px}' +
            'body.klb-locked{overflow:hidden!important;touch-action:none}';
        var style = document.createElement('style');
        style.id = 'kinsora-lightbox-styles';
        style.appendChild(document.createTextNode(css));
        document.head.appendChild(style);
    }

    /* ---------- collect images from hidden gallery anchors ---------- */
    function collectImages() {
        var anchors = document.querySelectorAll('.kinsora-pswp-gallery a');
        var list = [];
        for (var i = 0; i < anchors.length; i++) {
            var a = anchors[i];
            var thumb = a.querySelector('img');
            list.push({
                fullSrc: a.getAttribute('href') || '',
                srcset: a.getAttribute('data-pswp-srcset') || '',
                width: Number(a.getAttribute('data-pswp-width')) || 0,
                height: Number(a.getAttribute('data-pswp-height')) || 0,
                globalIndex: parseInt(a.getAttribute('data-global-index'), 10),
                alt: a.getAttribute('data-pswp-alt') || (thumb ? (thumb.getAttribute('alt') || '') : '')
            });
        }
        var media = window.Kinsora && window.Kinsora.productMedia || [];
        media.forEach(function(item, index) {
            if (item.type === 'video') list.push({ type: 'video', sources: item.sources || [], fullSrc: item.preview, alt: item.alt || '', globalIndex: index });
        });
        list.sort(function(a, b) { return a.globalIndex - b.globalIndex; });
        return list;
    }

    function currentGlobalIdx() {
        if (window.Kinsora && typeof window.Kinsora.currentImageIndex === 'number') {
            return window.Kinsora.currentImageIndex;
        }
        if (typeof window.currentImageIndex === 'number') return window.currentImageIndex;
        return 0;
    }

    function indexOfGlobal(globalIdx) {
        for (var i = 0; i < STATE.images.length; i++) {
            if (STATE.images[i].globalIndex === globalIdx) return i;
        }
        return 0;
    }

    /* ---------- build overlay ---------- */
    function buildOverlay() {
        var o = document.createElement('div');
        o.className = 'klb-overlay';
        o.setAttribute('role', 'dialog');
        o.setAttribute('aria-modal', 'true');
        o.setAttribute('aria-label', 'Enlarged product media');
        o.innerHTML =
            '<div class="klb-stage">' +
                '<div class="klb-track">' +
                    '<img class="klb-img" alt="" draggable="false">' +
                    '<video class="klb-video" tabindex="0" controls playsinline muted loop preload="metadata" hidden></video>' +
                    '<span class="klb-video-error" role="status" hidden>Video could not load. Please close and try again.</span>' +
                '</div>' +
            '</div>' +
            '<div class="klb-controls">' +
                '<div class="klb-navigation">' +
                '<button type="button" class="klb-arrow klb-arrow-prev" aria-label="Previous media">' +
                    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>' +
                '</button>' +
                '<button type="button" class="klb-arrow klb-arrow-next" aria-label="Next media">' +
                    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>' +
                '</button>' +
                '</div>' +
                '<span class="klb-counter" aria-live="polite">1 / 1</span>' +
                '<div class="klb-actions">' +
                    '<button type="button" class="klb-btn klb-zoom" aria-label="Toggle zoom">' +
                        '<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>' +
                    '</button>' +
                    '<button type="button" class="klb-btn klb-close" aria-label="Close">' +
                        '<svg viewBox="0 0 24 24"><line x1="6" y1="6" x2="18" y2="18"></line><line x1="6" y1="18" x2="18" y2="6"></line></svg>' +
                    '</button>' +
                '</div>' +
            '</div>';
        return o;
    }

    /* ---------- size image + stage to fill available space ----------
     * With `width:auto; height:auto` CSS, a low-resolution source
     * (e.g. an 800×800 product crop) renders at its intrinsic size
     * and looks tiny in the lightbox while a 2400×2400 shot fills
     * the viewport. We fix that here: every image is sized to the
     * largest box that fits within (viewport × viewport-minus-controls)
     * while preserving aspect. The stage fills the viewport above a stable controls bar
     * for both photos and video.
     */
    function fitImageAndStage() {
        if (!STATE.overlay) return;
        if (STATE.images[STATE.index] && STATE.images[STATE.index].type === 'video') return;
        var stage = STATE.overlay.querySelector('.klb-stage');
        var img = STATE.overlay.querySelector('.klb-img');
        var controls = STATE.overlay.querySelector('.klb-controls');
        if (!img || !stage) return;
        if (!img.naturalWidth || !img.naturalHeight) return; // wait for load

        var natAspect = img.naturalWidth / img.naturalHeight;
        var viewW = window.innerWidth;
        var viewH = window.innerHeight;
        var controlsH = controls ? controls.offsetHeight : 72;
        var maxH = Math.max(1, stage.clientHeight || viewH - controlsH);
        var maxW = stage.clientWidth || viewW;

        var displayW, displayH;
        if (maxH * natAspect <= maxW) {
            // Height-bound: image is taller than the max box's aspect
            displayH = maxH;
            displayW = maxH * natAspect;
        } else {
            // Width-bound: image is wider than the max box's aspect
            displayW = maxW;
            displayH = maxW / natAspect;
        }

        img.style.width = displayW + 'px';
        img.style.height = displayH + 'px';
    }

    function updateImageSizes(img, data) {
        if (!img || !data) return;
        var controls = STATE.overlay && STATE.overlay.querySelector('.klb-controls');
        var stage = STATE.overlay && STATE.overlay.querySelector('.klb-stage');
        var maxH = Math.max(1, stage && stage.clientHeight || window.innerHeight - (controls ? controls.offsetHeight : 72));
        var maxW = stage && stage.clientWidth || window.innerWidth;
        var width = data.width && data.height ? Math.min(maxW, maxH * data.width / data.height) : maxW;
        var sizes = Math.ceil(width * Math.max(1, STATE.scale)) + 'px';
        if (img.getAttribute('sizes') !== sizes) img.setAttribute('sizes', sizes);
    }

    /* ---------- render current slide ---------- */
    function renderSlide() {
        if (!STATE.overlay) return;
        var data = STATE.images[STATE.index];
        if (!data) return;
        var img = STATE.overlay.querySelector('.klb-img');
        var counter = STATE.overlay.querySelector('.klb-counter');

        // Reset transform so the new slide starts at scale 1, centered
        resetTransform(false);

        var video = STATE.overlay.querySelector('.klb-video');
        var isVideo = data.type === 'video';
        video.pause();
        video.removeAttribute('src');
        video.load();
        STATE.overlay.querySelector('.klb-video-error').hidden = true;
        STATE.overlay.classList.toggle('is-video', isVideo);
        video.hidden = !isVideo;
        img.hidden = isVideo;
        STATE.overlay.querySelector('.klb-zoom').hidden = isVideo;
        STATE.overlay.querySelector('.klb-stage').style.height = '';
        STATE.overlay.querySelector('.klb-track').style.transform = '';
        STATE.touch = null;
        STATE.pinch = null;
        if (isVideo) {
            img.onload = null;
            img.removeAttribute('src');
            img.removeAttribute('srcset');
            video.poster = data.fullSrc || '';
            video.setAttribute('aria-label', data.alt || 'Product video');
            video.muted = true;
            var sources = data.sources.filter(function(source) { return source.url && source.format === 'mp4'; }).sort(function(a, b) { return (b.height || 0) - (a.height || 0); });
            var source = sources[0] || data.sources.find(function(source) { return source.url && video.canPlayType(source.type || ''); });
            if (source) {
                video.src = source.url;
                var playing = video.play();
                if (playing && playing.catch) playing.catch(function() { /* Native controls allow manual playback. */ });
            } else STATE.overlay.querySelector('.klb-video-error').hidden = false;
        } else {
            // Size handler runs once the new source has decoded
            img.onload = fitImageAndStage;

            updateImageSizes(img, data);
            img.decoding = 'async';
            img.fetchPriority = 'high';
            if (data.srcset) img.setAttribute('srcset', data.srcset);
            else img.removeAttribute('srcset');
            img.src = data.fullSrc;
            img.alt = data.alt;

            // If the browser served from cache, naturalWidth is already set → fit immediately
            if (img.complete && img.naturalWidth) fitImageAndStage();

        }

        counter.textContent = (STATE.index + 1) + ' / ' + STATE.images.length;

        var prev = STATE.overlay.querySelector('.klb-arrow-prev');
        var next = STATE.overlay.querySelector('.klb-arrow-next');
        if (STATE.images.length <= 1) {
            prev.setAttribute('disabled', 'disabled');
            next.setAttribute('disabled', 'disabled');
        } else {
            prev.removeAttribute('disabled');
            next.removeAttribute('disabled');
        }
    }

    /* ---------- navigation ---------- */
    function go(dir) {
        if (STATE.images.length <= 1) return;
        STATE.index = (STATE.index + dir + STATE.images.length) % STATE.images.length;
        renderSlide();
    }
    function goPrev() { go(-1); }
    function goNext() { go(1); }

    /* ---------- transform helpers ----------
     * Single source of truth: STATE.scale, STATE.tx, STATE.ty.
     * All zoom / pinch / pan gestures mutate that trio and call applyTransform().
     * Using translate+scale with transform-origin 0 0 (top-left) keeps the math
     * uniform across tap-to-zoom, pinch-to-zoom, and one-finger panning.
     */
    function applyTransform(animate) {
        var img = STATE.overlay && STATE.overlay.querySelector('.klb-img');
        if (!img) return;
        updateImageSizes(img, STATE.images[STATE.index]);
        img.style.transformOrigin = '0 0';
        img.style.transition = animate ? '' : 'none';
        img.style.transform = 'translate(' + STATE.tx + 'px,' + STATE.ty + 'px) scale(' + STATE.scale + ')';
        if (STATE.scale > 1.01) img.classList.add('is-zoomed');
        else img.classList.remove('is-zoomed');
    }
    function resetTransform(animate) {
        STATE.scale = 1;
        STATE.tx = 0;
        STATE.ty = 0;
        applyTransform(animate !== false);
    }
    function getImageBase() {
        // Pre-transform rect of the image. Because transform-origin is 0 0 and
        // we translate+scale, rect.left = baseLeft + tx, rect.width = baseW * scale.
        var img = STATE.overlay && STATE.overlay.querySelector('.klb-img');
        if (!img) return null;
        var r = img.getBoundingClientRect();
        var s = STATE.scale || 1;
        return {
            left: r.left - STATE.tx,
            top: r.top - STATE.ty,
            width: r.width / s,
            height: r.height / s
        };
    }
    function clampPan() {
        if (!STATE.overlay) return;
        var b = getImageBase();
        if (!b) return;
        var viewW = window.innerWidth;
        var viewH = window.innerHeight;
        var controls = STATE.overlay && STATE.overlay.querySelector('.klb-controls');
        var controlsH = (controls && controls.offsetHeight) || 72;
        var visH = Math.max(100, viewH - controlsH); // visible area above controls
        var w = b.width * STATE.scale;
        var h = b.height * STATE.scale;

        if (w <= viewW) {
            STATE.tx = (viewW - w) / 2 - b.left;
        } else {
            var maxTx = -b.left;
            var minTx = viewW - b.left - w;
            STATE.tx = Math.max(minTx, Math.min(maxTx, STATE.tx));
        }
        if (h <= visH) {
            STATE.ty = (visH - h) / 2 - b.top;
        } else {
            var maxTy = -b.top;
            var minTy = visH - b.top - h;
            STATE.ty = Math.max(minTy, Math.min(maxTy, STATE.ty));
        }
    }

    /* ---------- tap / click zoom toggle ---------- */
    function toggleZoomAt(clientX, clientY) {
        if (STATE.images[STATE.index] && STATE.images[STATE.index].type === 'video') return;
        if (STATE.scale > 1.01) {
            resetTransform(true);
            return;
        }
        var b = getImageBase();
        if (!b) return;
        var s = DOUBLE_TAP_SCALE;
        var px = typeof clientX === 'number' ? clientX : (b.left + b.width / 2);
        var py = typeof clientY === 'number' ? clientY : (b.top + b.height / 2);
        // Pivot in image-local coords (pre-transform, because scale=1, tx=ty=0 right now)
        var imgX = px - b.left;
        var imgY = py - b.top;
        STATE.scale = s;
        // tx/ty chosen so that image point (imgX, imgY) lands back on screen (px, py)
        STATE.tx = px - b.left - imgX * s;
        STATE.ty = py - b.top - imgY * s;
        clampPan();
        applyTransform(true);
    }

    /* ---------- multi-touch gesture handling ---------- */
    function _dist(t0, t1) {
        var dx = t1.clientX - t0.clientX;
        var dy = t1.clientY - t0.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }
    function _mid(t0, t1) {
        return { x: (t0.clientX + t1.clientX) / 2, y: (t0.clientY + t1.clientY) / 2 };
    }

    function onTouchStart(e) {
        if (STATE.images[STATE.index] && STATE.images[STATE.index].type === 'video') return;
        STATE.lastTouchAt = Date.now();
        if (!STATE.overlay) return;
        if (e.touches.length >= 2) {
            // Begin pinch — wins over any active single-finger gesture
            STATE.touch = null;
            var track = STATE.overlay && STATE.overlay.querySelector('.klb-track');
            if (track) {
                track.classList.remove('is-dragging');
                track.style.transform = '';
            }

            var t0 = e.touches[0], t1 = e.touches[1];
            var mid = _mid(t0, t1);
            var b = getImageBase();
            if (!b) return;
            var imgX = (mid.x - b.left - STATE.tx) / STATE.scale;
            var imgY = (mid.y - b.top - STATE.ty) / STATE.scale;
            STATE.pinch = {
                startDist: _dist(t0, t1),
                startScale: STATE.scale,
                imgX: imgX,
                imgY: imgY,
                base: b
            };
            return;
        }
        if (e.touches.length === 1 && !STATE.pinch) {
            var t = e.touches[0];
            STATE.touch = {
                x: t.clientX, y: t.clientY, t: Date.now(),
                dx: 0, dy: 0, moved: false, dragging: false,
                startTx: STATE.tx, startTy: STATE.ty
            };
        }
    }

    function onTouchMove(e) {
        if (STATE.images[STATE.index] && STATE.images[STATE.index].type === 'video') return;
        if (!STATE.overlay) return;
        // Pinch takes precedence whenever 2+ fingers are down
        if (STATE.pinch && e.touches.length >= 2) {
            var t0 = e.touches[0], t1 = e.touches[1];
            var newMid = _mid(t0, t1);
            var newDist = _dist(t0, t1);
            var ratio = newDist / STATE.pinch.startDist;
            var newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, STATE.pinch.startScale * ratio));
            var b = STATE.pinch.base;
            STATE.scale = newScale;
            STATE.tx = newMid.x - b.left - STATE.pinch.imgX * newScale;
            STATE.ty = newMid.y - b.top - STATE.pinch.imgY * newScale;
            applyTransform(false);
            return;
        }
        if (!STATE.touch || STATE.pinch) return;
        if (e.touches.length !== 1) return;
        var t = e.touches[0];
        var dx = t.clientX - STATE.touch.x;
        var dy = t.clientY - STATE.touch.y;
        STATE.touch.dx = dx;
        STATE.touch.dy = dy;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) STATE.touch.moved = true;

        if (STATE.scale > 1.01) {
            // Pan the zoomed image — move with finger, live (no transition)
            STATE.tx = STATE.touch.startTx + dx;
            STATE.ty = STATE.touch.startTy + dy;
            applyTransform(false);
            return;
        }

        // Not zoomed: horizontal drag previews next/prev slide
        var track = STATE.overlay && STATE.overlay.querySelector('.klb-track');
        if (!track) return;
        if (!STATE.touch.dragging && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
            STATE.touch.dragging = true;
            track.classList.add('is-dragging');
        }
        if (STATE.touch.dragging) {
            track.style.transform = 'translateX(' + dx + 'px)';
        }
    }

    function onTouchEnd(e) {
        if (STATE.images[STATE.index] && STATE.images[STATE.index].type === 'video') return;
        STATE.lastTouchAt = Date.now();
        if (!STATE.overlay) return;
        // Ending pinch: when we drop below 2 touches, settle the transform
        if (STATE.pinch && (!e.touches || e.touches.length < 2)) {
            STATE.pinch = null;
            if (STATE.scale <= 1.02) {
                resetTransform(true);
            } else {
                clampPan();
                applyTransform(true);
            }
            // If one finger is still down, start a pan session from here
            if (e.touches && e.touches.length === 1) {
                var ft = e.touches[0];
                STATE.touch = {
                    x: ft.clientX, y: ft.clientY, t: Date.now(),
                    dx: 0, dy: 0, moved: true, dragging: false,
                    startTx: STATE.tx, startTy: STATE.ty
                };
            }
            return;
        }
        if (!STATE.touch) return;
        var touch = STATE.touch;
        STATE.touch = null;
        var track = STATE.overlay && STATE.overlay.querySelector('.klb-track');
        if (track) {
            track.classList.remove('is-dragging');
            track.style.transform = '';
        }

        var absX = Math.abs(touch.dx);
        var absY = Math.abs(touch.dy);
        var dt = Date.now() - touch.t;

        // Quick tap with no real movement → toggle zoom at the tap point
        if (!touch.moved && dt < 350) {
            var target = e.target;
            if (target && target.closest && (target.closest('.klb-btn') || target.closest('.klb-arrow') || target.closest('.klb-controls'))) {
                return;
            }
            toggleZoomAt(touch.x, touch.y);
            return;
        }

        // Finished a pan while zoomed → clamp back into sensible bounds
        if (STATE.scale > 1.01) {
            clampPan();
            applyTransform(true);
            return;
        }

        // Not zoomed: horizontal swipe navigates, downward swipe closes
        if (absX > 60 && absX > absY) {
            if (touch.dx < 0) goNext();
            else goPrev();
            return;
        }
        if (touch.dy > 110 && absY > absX) {
            close();
        }
    }

    /* ---------- events ---------- */
    function onKey(e) {
        if (!STATE.overlay) return;
        if (e.key === 'Tab') {
            var focusable = Array.prototype.filter.call(STATE.overlay.querySelectorAll('button, video[controls]'), function(el) { return !el.hidden && !el.disabled && window.getComputedStyle(el).display !== 'none'; });
            var first = focusable[0], last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
            return;
        }
        if (e.key === 'Escape') { e.preventDefault(); close(); }
        else if (e.target && e.target.tagName === 'VIDEO') return;
        else if (e.key === 'ArrowLeft') goPrev();
        else if (e.key === 'ArrowRight') goNext();
    }

    function wireEvents() {
        var o = STATE.overlay;
        if (!o) return;
        o.querySelector('.klb-close').addEventListener('click', close);
        o.querySelector('.klb-zoom').addEventListener('click', function () { toggleZoomAt(); });
        o.querySelector('.klb-arrow-prev').addEventListener('click', goPrev);
        o.querySelector('.klb-arrow-next').addEventListener('click', goNext);

        o.querySelector('.klb-video').addEventListener('error', function() {
            if (this.hasAttribute('src')) o.querySelector('.klb-video-error').hidden = false;
        });
        var img = o.querySelector('.klb-img');
        img.addEventListener('click', function (e) {
            // Desktop click-to-zoom-at-cursor (mobile uses touchend tap path)
            if (Date.now() - STATE.lastTouchAt < 700) return;
            toggleZoomAt(e.clientX, e.clientY);
        });

        var stage = o.querySelector('.klb-stage');
        stage.addEventListener('touchstart', onTouchStart, { passive: true });
        stage.addEventListener('touchmove', onTouchMove, { passive: true });
        stage.addEventListener('touchend', onTouchEnd);
        stage.addEventListener('touchcancel', function () {
            STATE.touch = null;
            STATE.pinch = null;
            var tr = o.querySelector('.klb-track');
            tr.classList.remove('is-dragging');
            tr.style.transform = '';
        });

        // Click on backdrop (the stage, outside the image) closes
        stage.addEventListener('click', function (e) {
            if (Date.now() - STATE.lastTouchAt < 700) return; // mobile handled by touchend
            if (e.target === stage) close();
        });

        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
    }

    function unwireEvents() {
        document.removeEventListener('keydown', onKey);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onResize);
    }

    var _resizeTimer = null;
    function onResize() {
        if (_resizeTimer) clearTimeout(_resizeTimer);
        _resizeTimer = setTimeout(function() {
            if (STATE.overlay) updateImageSizes(STATE.overlay.querySelector('.klb-img'), STATE.images[STATE.index]);
            fitImageAndStage();
        }, 60);
    }

    /* ---------- open / close ---------- */
    function open(globalIdx) {
        var requestedIndex = typeof globalIdx === 'number' ? globalIdx : currentGlobalIdx();
        if (STATE.overlay) return;
        STATE.images = collectImages();
        if (currentGlobalIdx() < 0) {
            var displayedImage = document.getElementById('mainImage');
            if (displayedImage && displayedImage.src) {
                STATE.images = [{
                    fullSrc: displayedImage.src,
                    srcset: displayedImage.srcset || '',
                    width: displayedImage.naturalWidth || 0,
                    height: displayedImage.naturalHeight || 0,
                    globalIndex: -1,
                    alt: displayedImage.alt || ''
                }];
            }
        }
        if (!STATE.images.length) return;

        var container = document.getElementById('mainMediaContainer');
        var inline = container.querySelector('[data-gallery-player]');
        STATE.resumeInline = !!inline && !inline.paused && container.dataset.galleryKind === 'video';
        var pause = container.querySelector('[data-gallery-pause]');
        if (pause && container.dataset.galleryKind === 'video') pause.click();
        STATE.returnFocus = container.dataset.galleryKind === 'video' ? container.querySelector('[data-gallery-expand]') : container;
        STATE.index = indexOfGlobal(requestedIndex);
        STATE.overlay = buildOverlay();
        document.body.appendChild(STATE.overlay);
        STATE.bodyOverflow = document.body.style.overflow;
        document.body.classList.add('klb-locked');

        renderSlide();
        wireEvents();

        // Trigger transition on next frame
        requestAnimationFrame(function () {
            if (STATE.overlay) STATE.overlay.classList.add('is-open');
        });

        var closeBtn = STATE.overlay && STATE.overlay.querySelector('.klb-close');
        if (closeBtn && closeBtn.focus) { try { closeBtn.focus({ preventScroll: true }); } catch (e) { closeBtn.focus(); } }
    }

    function close() {
        if (!STATE.overlay) return;
        var o = STATE.overlay;
        var returnFocus = STATE.returnFocus;
        var video = o.querySelector('.klb-video');
        video.pause();
        video.removeAttribute('src');
        video.load();
        if (STATE.resumeInline && !document.hidden) {
            var play = document.querySelector('#mainMediaContainer[data-gallery-kind="video"] [data-gallery-play]');
            if (play) play.click();
        }
        STATE.resumeInline = false;
        o.classList.remove('is-open');
        unwireEvents();
        document.body.classList.remove('klb-locked');
        document.body.style.overflow = STATE.bodyOverflow;
        setTimeout(function () {
            if (o && o.parentNode) o.parentNode.removeChild(o);
            if (returnFocus && document.documentElement.contains(returnFocus) && returnFocus.focus) {
                try { returnFocus.focus({ preventScroll: true }); } catch (e) { returnFocus.focus(); }
            }
        }, 220);
        STATE.overlay = null;
        STATE.touch = null;
        STATE.pinch = null;
        STATE.scale = 1;
        STATE.tx = 0;
        STATE.ty = 0;
        STATE.returnFocus = null;
    }

    /* ---------- mobile "Tap to zoom" hint ---------- */
    function maybeShowMobileHint(container) {
        try {
            if (window.matchMedia && !window.matchMedia('(hover: none)').matches) return;
            if (window.matchMedia && window.matchMedia('(pointer: fine)').matches) return;
        } catch (e) { /* no-op */ }

        var mode = (container.getAttribute('data-zoom-hint') || 'persistent').toLowerCase();
        if (mode === 'off') return;

        if (mode === 'once') {
            try {
                if (window.localStorage && window.localStorage.getItem('kinsora_pswp_hint_seen') === '1') return;
            } catch (e) { /* no-op */ }
        }

        var label = container.getAttribute('data-zoom-hint-label') || 'Tap to zoom';
        var side = (container.getAttribute('data-zoom-hint-side') || 'right').toLowerCase();
        var hint = document.createElement('div');
        hint.className = 'kinsora-zoom-hint'
            + (mode === 'persistent' ? ' kinsora-zoom-hint--persistent' : '')
            + (side === 'left' ? ' kinsora-zoom-hint--left' : '');
        hint.setAttribute('aria-hidden', 'true');
        hint.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>' +
            '<span></span>';
        hint.querySelector('span').textContent = label;
        container.appendChild(hint);

        setTimeout(function () { hint.classList.add('is-visible'); }, 400);

        if (mode === 'once') {
            setTimeout(function () {
                hint.classList.remove('is-visible');
                setTimeout(function () { if (hint.parentNode) hint.parentNode.removeChild(hint); }, 400);
            }, 3800);
            try { if (window.localStorage) window.localStorage.setItem('kinsora_pswp_hint_seen', '1'); } catch (e) { /* no-op */ }
        }
    }

    /* ---------- init ---------- */
    function init() {
        var container = document.getElementById('mainMediaContainer');
        if (!container) return;
        if (!document.querySelector('.kinsora-pswp-gallery a') && !(window.Kinsora && window.Kinsora.productMedia && window.Kinsora.productMedia.length)) return;

        injectStyles();
        container.classList.add('kinsora-zoomable');

        container.addEventListener('click', function (e) {
            if (container.dataset.galleryKind === 'video') {
                if (e.target.closest('[data-gallery-expand]') || e.target.closest('video')) open(currentGlobalIdx());
                return;
            }
            var t = e.target;
            if (t && t.closest && (t.closest('video') || t.closest('iframe') || t.closest('model-viewer'))) return;
            if (t && (t.tagName === 'VIDEO' || t.tagName === 'IFRAME' || t.tagName === 'MODEL-VIEWER')) return;
            open(currentGlobalIdx());
        });

        if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '0');
        container.setAttribute('role', container.dataset.galleryKind === 'video' ? 'group' : 'button');
        if (container.dataset.galleryKind === 'video') container.removeAttribute('tabindex');
        if (container.dataset.galleryKind !== 'video') container.setAttribute('aria-label', container.dataset.galleryZoomLabel || 'Open image in full screen to zoom');
        container.addEventListener('keydown', function (e) {
            if (container.dataset.galleryKind === 'video' || e.target !== container) return;
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                e.preventDefault();
                open(currentGlobalIdx());
            }
        });

        maybeShowMobileHint(container);

        // Expose a tiny API (useful if any external code wants to open programmatically)
        window.KinsoraLightbox = {
            open: open,
            close: close,
            updateLabel: function(label) {
                if (label && container.dataset.galleryKind !== 'video') container.setAttribute('aria-label', label);
            }
        };

    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
